import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Automation, AutomationType } from '@vendasta/automata';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { AutomationEditorPreviewComponent } from '../../automation-editor/automation-editor-preview.component';
import { AutomationService } from '../../automation/automation.service';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { take } from 'rxjs/operators';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AutomationEditorService } from '../../automation-editor/automation-editor.service';
import { AutomationViewService } from '../../automation/automation-view.service';
import { AutomationGraphUtilitiesService } from '../../automation-editor/automation-graph-utilities.service';

export interface PreviewDialogData {
  automation: Automation;
  onlyShowCloseButton?: boolean;
  warningMessage?: string;
  snippetEmitter?: EventEmitter<Automation>;
}

@Component({
  templateUrl: './automation-preview-dialog.component.html',
  styleUrls: ['./automation-preview-dialog.component.scss'],
  providers: [AutomationEditorService, AutomationViewService, AutomationGraphUtilitiesService],
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    GalaxyAlertModule,
    MatRadioModule,
    MatFormFieldModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyPipesModule,
    AutomationEditorPreviewComponent,
    GalaxyButtonLoadingIndicatorModule,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class AutomationPreviewDialogComponent implements OnInit {
  automation: Automation;
  isProcessing = false;
  onlyShowCloseButton = false;
  isSnippet = false;
  warningMessage = '';

  constructor(
    public dialogRef: MatDialogRef<AutomationPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: PreviewDialogData,
    private readonly automationService: AutomationService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.automation = this.data.automation;
    this.onlyShowCloseButton = this.data.onlyShowCloseButton || false;
    this.warningMessage = this.data?.warningMessage ? this.data.warningMessage : '';
    this.isSnippet = this.data?.automation?.isSnippetAutomation;
  }

  createFromTemplate(selectedTemplate: Automation): void {
    this.isProcessing = true;

    this.automationService.createFromSource(selectedTemplate).subscribe({
      next: () => {
        this.dialogRef.close();
      },
      error: () => (this.isProcessing = false),
    });
  }

  deleteSnippet(snippet: Automation): void {
    this.isProcessing = true;
    this.automationService
      .deleteAutomation(snippet, AutomationType.AUTOMATION_TYPE_SNIPPET)
      .pipe(take(1))
      .subscribe({
        next: (succeeded) => {
          if (succeeded) {
            this.dialogRef.close();
            this.snackbarService.openSuccessSnack(
              this.translateService.instant('AUTOMATIONS.PREVIEW_SNIPPET_DIALOG.DELETE_SUCCESS_MESSAGE'),
            );
          }
        },
        error: this.displayError.bind(this),
      })
      .add(() => (this.isProcessing = false));
  }

  private displayError(): void {
    this.snackbarService.openErrorSnack(
      this.translateService.instant('AUTOMATIONS.PREVIEW_SNIPPET_DIALOG.DELETE_ERROR_MESSAGE'),
    );
  }

  useSnippet(): void {
    this.data.snippetEmitter?.emit(this.automation);
  }
}
